import { supabase } from '../../../lib/supabase/client';
import type { Reminder, ReminderFormData } from '../types';

export const reminderService = {
  /**
   * Tüm hatırlatıcıları getir
   */
  async getReminders(): Promise<Reminder[]> {
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .order('reminder_date', { ascending: true });

    if (error) {
      console.error('Reminders fetch error:', error);
      throw error;
    }

    // PostgreSQL'den gelen veriyi frontend formatına çevir
    return (data || []).map(reminder => ({
      _id: reminder.id,
      userId: reminder.user_id,
      title: reminder.title,
      description: reminder.description || '',
      reminderDate: reminder.reminder_date,
      isCompleted: reminder.is_completed,
      createdAt: reminder.created_at,
      updatedAt: reminder.updated_at,
    }));
  },

  /**
   * Tek bir hatırlatıcıyı getir
   */
  async getReminder(id: string): Promise<Reminder> {
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Reminder fetch error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      reminderDate: data.reminder_date,
      isCompleted: data.is_completed,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Yeni hatırlatıcı oluştur
   */
  async createReminder(reminderData: ReminderFormData): Promise<Reminder> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Kullanıcı oturumu bulunamadı');
    }

    const { data, error } = await supabase
      .from('reminders')
      .insert({
        user_id: user.id,
        title: reminderData.title,
        description: reminderData.description || null,
        reminder_date: reminderData.reminderDate,
        is_completed: reminderData.isCompleted || false,
      })
      .select()
      .single();

    if (error) {
      console.error('Reminder create error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      reminderDate: data.reminder_date,
      isCompleted: data.is_completed,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Hatırlatıcıyı güncelle
   */
  async updateReminder(id: string, reminderData: Partial<ReminderFormData> & { isCompleted?: boolean }): Promise<Reminder> {
    const updateData: any = {};

    if (reminderData.title !== undefined) updateData.title = reminderData.title;
    if (reminderData.description !== undefined) updateData.description = reminderData.description || null;
    if (reminderData.reminderDate !== undefined) updateData.reminder_date = reminderData.reminderDate;
    if (reminderData.isCompleted !== undefined) updateData.is_completed = reminderData.isCompleted;

    const { data, error } = await supabase
      .from('reminders')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Reminder update error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      reminderDate: data.reminder_date,
      isCompleted: data.is_completed,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Hatırlatıcıyı sil
   */
  async deleteReminder(id: string): Promise<void> {
    const { error } = await supabase
      .from('reminders')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Reminder delete error:', error);
      throw error;
    }
  },

  /**
   * Hatırlatıcı durumunu değiştir (tamamlandı/bekliyor)
   */
  async toggleReminderComplete(id: string, isCompleted: boolean): Promise<Reminder> {
    return this.updateReminder(id, { isCompleted });
  },

  /**
   * Aktif hatırlatıcıları getir (tamamlanmamış ve gelecek tarihli)
   */
  async getActiveReminders(): Promise<Reminder[]> {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .eq('is_completed', false)
      .gte('reminder_date', now)
      .order('reminder_date', { ascending: true });

    if (error) {
      console.error('Active reminders fetch error:', error);
      throw error;
    }

    return (data || []).map(reminder => ({
      _id: reminder.id,
      userId: reminder.user_id,
      title: reminder.title,
      description: reminder.description || '',
      reminderDate: reminder.reminder_date,
      isCompleted: reminder.is_completed,
      createdAt: reminder.created_at,
      updatedAt: reminder.updated_at,
    }));
  },
};

